import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type ReactNode } from 'react'
import { PointsPrice } from '@/components/features/points/PointsPrice'
import { canRedeemNow, isFreeProduct, pricingModeOf } from '@/components/features/points/productPoints'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Infinity, Layers, Puzzle, RefreshCw, Zap } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CoverImage } from '@/components/ui/cover-image'
import { usePriceLabel } from '@/components/features/products/usePriceLabel'
import { PAYMENT_METHOD_ICONS, productCover } from '@/lib/checkout/productDisplay'
import { parseAllowedPaymentMethods } from '@/lib/checkout/paymentMethods'
import { savingsOf } from '@/lib/checkout/annual'
import { YearlyPrice } from './YearlyPrice'
import { useYearlyPriceParts } from './useYearlyPriceParts'
import { AppLink } from '@/components/ui/app-link'
import { iconComponent } from '@/components/ui/icon-picker'
import { useMenu } from '@/lib/MenuContext'
import { moduleIcon } from '@/modules/registry'
import { spacePathOf } from '@/components/features/parcels/parcelUtils'
import { cn } from '@/lib/utils'
import { useMoney } from '@/lib/hooks/useMoney'
import { useMemberEntry } from '@/lib/hooks/useMemberEntry'
import type { PublicProduct } from '@/types/checkout'
import type { StorefrontAddOn, StorefrontPlan } from '@/types/storefront'
import type { StoreCardLayout, StoreItemKind } from './storefrontDisplay'
import { KIND_ICON, intervalLabelKey, planBenefitLines, planIsRecommended, planOptionFor, type PlanOption } from './storefrontDisplay'

/* The buttons live inside a clickable card: they act on their own, without
   also opening the details behind them. */
const stop = (e: MouseEvent | KeyboardEvent) => e.stopPropagation()

/* Store images are 16:9 (2000×1125 on upload): asked of the CDN at the card's width, already in 16:9. */
const CARD_COVER_CDN = { w: 640, h: 360, fit: 'crop' } as const
const WIDE_COVER_CDN = { w: 960, h: 540, fit: 'crop' } as const

/** The kind as a small label with its icon, in the community's color. */
function KindTag({ kind }: { kind: StoreItemKind }) {
  const { t } = useTranslation('common')
  const Icon = KIND_ICON[kind]
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-primary-700">
      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
      {t(`storefront.kind.${kind}`)}
    </span>
  )
}

interface StoreCardShellProps {
  kind: StoreItemKind
  layout: StoreCardLayout
  title: string
  description?: string | null
  /** Plans have no image: they are typographic cards, never a placeholder that looks broken. */
  cover?: string | null
  badge?: { label: string; tone: 'primary' | 'muted' } | null
  highlighted?: boolean
  dimmed?: boolean
  price: ReactNode
  /** Under the description: a plan's lines from its description. */
  body?: ReactNode
  action: ReactNode
  onOpen?: () => void
  testId: string
  data?: Record<string, string>
  /** Other cards in the grid have a cover: this one takes the same slot, typographic, so the rows line up. */
  coverSlot?: boolean
}

/**
 * The one card every store item uses (David, 2026-10-30: "isso não é uma forma
 * inteligente de mostrar produtos"): type, name, what it is, a price that
 * reads first, and one solid action at the bottom. A product or add-on with
 * an image shows it as a 3:1 cover; without one the card is typographic.
 */
function StoreCardShell({ kind, layout, title, description, cover, badge, highlighted, dimmed, price, body, action, onOpen, testId, data, coverSlot }: StoreCardShellProps) {
  const wide = layout === 'wide'
  const badgeEl = badge && (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium',
        badge.tone === 'primary' ? 'bg-primary text-white' : 'theme-light bg-gray-900/80 text-white'
      )}
    >
      {badge.label}
    </span>
  )

  return (
    <div
      role={onOpen ? 'button' : undefined}
      tabIndex={onOpen ? 0 : undefined}
      aria-label={title}
      onClick={onOpen}
      onKeyDown={onOpen ? (e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      } : undefined}
      data-testid={testId}
      data-kind={kind}
      data-layout={layout}
      {...Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v]))}
      className={cn(
        'group flex flex-col overflow-hidden rounded-xl border bg-white transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        onOpen && 'cursor-pointer',
        highlighted ? 'border-primary ring-1 ring-primary-200' : 'border-gray-200',
        wide && cover && 'xl:flex-row'
      )}
    >
      {!cover && coverSlot && !wide && (
        /* No image: the name's initial on the community's tint, never a placeholder that looks like a failed image. */
        <div className="relative flex aspect-video flex-shrink-0 items-end overflow-hidden bg-primary-50 px-5" aria-hidden="true" data-testid="store-card-monogram">
          <span className="-mb-3 select-none text-7xl font-semibold leading-none text-primary-200">{title.trim().charAt(0).toUpperCase()}</span>
          {badgeEl && <span className="absolute left-3 top-3">{badgeEl}</span>}
        </div>
      )}
      {cover && (
        /* 16:9, the ratio the image is uploaded in (2000×1125): whole, never stretched. On the wide card the
           image is the left 40% from xl (narrower, it sits on top), flush with the card's edges: the card is as tall as the 16:9 image, and only
           when the text beside it is taller does the image grow with it (covering, centered). */
        <div className={cn('relative flex-shrink-0 overflow-hidden', wide && 'xl:w-2/5', dimmed && 'opacity-70')}>
          <div className="aspect-video" aria-hidden="true" />
          <CoverImage src={cover} alt="" className="absolute inset-0 h-full w-full object-cover object-center" cdn={wide ? WIDE_COVER_CDN : CARD_COVER_CDN} />
          {badgeEl && <span className="absolute left-3 top-3">{badgeEl}</span>}
        </div>
      )}
      <div className={cn('flex min-w-0 flex-1 flex-col p-5', wide && 'md:p-6 lg:p-8', wide && cover && 'xl:justify-center', wide && !cover && 'md:grid md:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] md:items-center md:gap-x-10')}>
        <div className="min-w-0">
          <div className="mb-2 flex items-center justify-between gap-2">
            <KindTag kind={kind} />
            {!cover && !(coverSlot && !wide) && badgeEl}
          </div>
          <h3 className={cn('font-semibold text-gray-900 group-hover:text-primary', wide ? 'text-xl md:text-2xl' : 'line-clamp-2 text-lg')}>{title}</h3>
          {description && (
            <p className={cn('mt-1.5 text-sm text-gray-500', wide ? 'line-clamp-4 md:text-base' : 'line-clamp-3')}>{description}</p>
          )}
          {body}
        </div>
        <div className={cn('mt-auto pt-5', wide && cover && 'xl:mt-0 xl:pt-6', wide && !cover && 'md:mt-0 md:pt-0')}>
          {price}
          <div className={cn('mt-4', wide && 'md:max-w-xs')}>{action}</div>
        </div>
      </div>
    </div>
  )
}

/** The amount, big, with its period or payment terms beside it. */
function PriceLine({ amount, suffix, wide }: { amount: string; suffix?: string | null; wide?: boolean }) {
  return (
    <p className="flex flex-wrap items-baseline gap-x-1.5">
      <span className={cn('font-semibold tracking-tight text-gray-900', wide ? 'text-3xl' : 'text-2xl')}>{amount}</span>
      {suffix && <span className="text-sm text-gray-500">{suffix}</span>}
    </p>
  )
}

/** A text where the button would be, same height, for "sold out", "you already have it" and the like. */
const ActionNote = ({ children, tone = 'muted', testId }: { children: ReactNode; tone?: 'muted' | 'success'; testId?: string }) => (
  <p
    className={cn(
      'flex h-10 items-center justify-center gap-1.5 rounded-lg text-sm font-medium',
      tone === 'success' ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-500'
    )}
    data-testid={testId}
  >
    {tone === 'success' && <Check className="h-4 w-4" />}
    {children}
  </p>
)

/**
 * "Liberado logo após a confirmação do pagamento.", under a plan's buying
 * button: the same line on the plans page, the Store and a padlocked space's
 * offer. `hidden` keeps its height (a card whose button is "Seu plano"), so
 * the buttons of a row still line up.
 */
export function AccessNote({ className, hidden = false }: { className?: string; hidden?: boolean }) {
  const { t } = useTranslation('common')
  return (
    <p className={cn('text-center text-xs text-gray-500', hidden && 'invisible', className)} aria-hidden={hidden || undefined} data-testid="plan-access-note">
      {t('access.offer.accessNote')}
    </p>
  )
}

/**
 * The buying button: the community's primary color, solid, with the text color
 * the palette computes for contrast (`text-primary-foreground`; AA with yellow,
 * red and navy palettes). Every purchase action uses it, add-ons included
 * (David, 2026-10-30: a pale tint read as weak); the add-on ranks below a plan
 * by its compact row, not by a duller button.
 */
const ActionButton = ({ label, onClick, disabled, testId, wide, weight = 'strong' }: { label: string; onClick: () => void; disabled?: boolean; testId: string; wide?: boolean; weight?: 'strong' | 'quiet' }) => (
  <Button
    variant="primary"
    size={wide ? 'lg' : 'default'}
    className={cn('h-10 w-full', weight === 'quiet' && 'shadow-sm')}
    disabled={disabled}
    onClick={(e) => {
      stop(e)
      onClick()
    }}
    onKeyDown={stop}
    data-testid={testId}
    data-weight={weight}
    title={label}
  >
    {/* A long plan name in the label ("Assinar {plan}") ends in an ellipsis, never past the button. */}
    <span className="min-w-0 truncate">{label}</span>
  </Button>
)

interface ProductStoreCardProps {
  /** Some card in the same grid has a cover image. */
  coverSlot?: boolean
  product: PublicProduct
  layout?: StoreCardLayout
  /** No "Destaque" (a store's own emphasis), where the card is one way among others to open a space. */
  plain?: boolean
  onOpen: () => void
  onBuy: () => void
  /** F-160: redeem with points (products that take points). */
  onRedeem?: () => void
}

/** A one-off product: price, installments and payment methods as checkout offers them, and "Buy". */
export function ProductStoreCard({ product, layout = 'grid', coverSlot, plain = false, onOpen, onBuy, onRedeem }: ProductStoreCardProps) {
  const { t } = useTranslation(['checkout', 'products', 'common', 'points'])
  const priceLabel = usePriceLabel()
  const { suspended: salesClosed } = useMemberEntry()
  const soldOut = Boolean(product.soldOut)
  const methods = parseAllowedPaymentMethods(product.allowedPaymentMethods)
  const installments = Number(product.maxInstallments) || 1
  /* F-160: "free" is the server's call (a points-only product keeps price 0 and is not free). */
  const free = isFreeProduct(product)
  const mode = pricingModeOf(product)
  const pointsOnly = mode === 'POINTS'
  const canRedeem = canRedeemNow(product)
  const wide = layout === 'wide'
  const terms = product.type === 'SUBSCRIPTION' || product.billingInterval
    ? t(product.billingInterval === 'YEARLY' ? 'perYear' : 'perMonth')
    : free ? null : installments > 1
      ? `${t('purchase.singlePayment')} · ${t('purchase.upToInstallments', { count: installments })}`
      : t('purchase.singlePayment')

  return (
    <StoreCardShell
      kind="product"
      layout={layout}
      coverSlot={coverSlot}
      title={product.title}
      description={product.shortDescription}
      cover={productCover(product)}
      highlighted={!plain && product.isHighlighted && !soldOut}
      dimmed={soldOut}
      badge={soldOut ? { label: t('purchase.soldOut'), tone: 'muted' } : product.isHighlighted && !plain ? { label: t('purchase.featured'), tone: 'primary' } : null}
      onOpen={onOpen}
      testId="product-card"
      price={
        <>
          {!pointsOnly && <PriceLine amount={priceLabel(product.price, product.currency)} suffix={terms} wide={wide} />}
          <PointsPrice product={product} className={pointsOnly ? undefined : 'mt-1'} />
          {methods.length > 0 && !free && !pointsOnly && (
            <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500">
              {methods.map((method) => {
                const Icon = PAYMENT_METHOD_ICONS[method]
                return (
                  <span key={method} className="inline-flex items-center gap-1">
                    <Icon className="h-3.5 w-3.5" />
                    {t(`purchase.methods.${method}`)}
                  </span>
                )
              })}
            </p>
          )}
        </>
      }
      action={
        salesClosed ? (
          /* F-86: the community is suspended; nothing new is sold. */
          <ActionNote testId="product-card-unavailable">{t('common:memberEntry.unavailable')}</ActionNote>
        ) : soldOut ? (
          <ActionNote testId="product-card-sold-out">{t('purchase.soldOut')}</ActionNote>
        ) : pointsOnly ? (
          <ActionButton
            label={t('points:card.redeem')}
            onClick={() => onRedeem?.()}
            disabled={!canRedeem || !onRedeem}
            testId="product-card-redeem"
            wide={wide}
          />
        ) : (
          <div className="flex min-w-0 flex-col gap-2">
            <ActionButton
              label={free ? t('products:freeClaim.cta') : t('purchase.buy')}
              onClick={onBuy}
              testId="product-card-buy"
              wide={wide}
            />
            {mode === 'POINTS_OR_MONEY' && onRedeem && (
              <ActionButton label={t('points:card.redeem')} onClick={onRedeem} disabled={!canRedeem} testId="product-card-redeem" wide={wide} weight="quiet" />
            )}
          </div>
        )
      }
    />
  )
}

interface PlanStoreCardProps {
  plan: StorefrontPlan
  /** The billing interval the store's toggle shows; null when there is no toggle. */
  interval: string | null
  layout?: StoreCardLayout
  /** The billing toggle, inside the card when the store has a single plan. */
  intervalSwitch?: ReactNode
  /** The benefit lines are shown next to the card (one plan): the card keeps to name, price and action. */
  hideBenefits?: boolean
  /** The only plan on show: no "Recommended" (recommended over what?). */
  alone?: boolean
  /** The button's text instead of "Subscribe" (the plans page names the plan, or the plan change). */
  actionLabel?: string
  /** Set when this is the viewer's plan: this label as its badge and in place of the button. */
  currentLabel?: string | null
  /** No "Plan" eyebrow above the name (a page where everything is a plan). */
  hideKind?: boolean
  /** A short warning above the button (F-195: the add-ons the viewer has that this plan does not take). */
  warning?: ReactNode
  onSubscribe: (plan: StorefrontPlan, option: PlanOption | null) => void
}

/**
 * A plan as a pricing card (F-29 phase 3, criteria 2-3; David, 2026-10-30:
 * "diferenciar o que é um produto, uma assinatura e um adicional"). Laid out
 * like a pricing table: name, a big price with its period and how it renews,
 * what the plan includes, and "Subscribe" as the strongest button of the
 * store, opening the plan's page on the option at the chosen interval. The
 * API gives a plan no list of benefits: the lines come from the description
 * the community wrote, one per line, never made up here.
 */
export function PlanStoreCard({ plan, interval, layout = 'grid', intervalSwitch, hideBenefits, alone = false, actionLabel, currentLabel = null, hideKind = false, warning = null, onSubscribe }: PlanStoreCardProps) {
  const { t } = useTranslation(['common', 'checkout'])
  const formatMoney = useMoney()
  const option = planOptionFor(plan, interval)
  const current = Boolean(currentLabel)
  const recommended = !alone && !current && planIsRecommended(plan)
  const wide = layout === 'wide'
  const periodKey = intervalLabelKey(option?.billingInterval)
  /* Without a toggle, a plan with several options shows its lowest price as "from". */
  const from = !interval && plan.options.length > 1
  const lines = planBenefitLines(plan)
  const list = lines.length > 1
  /* The yearly option's saving, the same number the checkout shows (the API's, API-189). */
  const yearlySaving = option?.billingInterval === 'YEARLY' ? savingsOf(option) : null
  const commitment = Number(option?.commitmentMonths) || 0
  const trialDays = Number(option?.trialDays) || 0
  const subscribe = () => {
    if (!current) onSubscribe(plan, option)
  }
  /* A plan has no image of its own: it shows its options' cover (the chosen one first), when there is one. */
  const optionCover = (o: PlanOption | null) => (o ? o.bannerUrl || o.featuredImageUrl || null : null)
  const cover = optionCover(option) || plan.options.map(optionCover).find(Boolean) || null

  const benefits = list && !hideBenefits ? (
    <ul className="mt-4 space-y-2 text-sm text-gray-700" data-testid="storefront-plan-benefits">
      {lines.slice(0, 8).map((line, index) => (
        <li key={index} className="flex gap-2">
          <Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary-600" aria-hidden="true" />
          <span>{line}</span>
        </li>
      ))}
    </ul>
  ) : undefined

  const price = (
    <>
      {intervalSwitch && <div className="mb-3" onClick={stop} onKeyDown={stop}>{intervalSwitch}</div>}
      {trialDays > 0 && (
        <p className="mb-2 inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
          {t('common:storefront.trialDays', { count: trialDays })}
        </p>
      )}
      {from && <p className="text-xs text-gray-500">{t('common:storefront.fromPrice')}</p>}
      {option?.billingInterval === 'YEARLY' ? (
        /* Yearly: as the plan's checkout shows it, "12× de R$ X" on the card and the up-front amount. */
        <YearlyPrice option={option} amount={Number(option.price)} currency={plan.currency} saving={yearlySaving} />
      ) : (
        <PriceLine
          amount={option ? formatMoney(Number(option.price), plan.currency) : '—'}
          suffix={periodKey ? t(periodKey) : option?.title}
          wide
        />
      )}
      {interval === 'YEARLY' && option && option.billingInterval !== 'YEARLY' && (
        <p className="mt-1 text-xs text-gray-500" data-testid="storefront-plan-no-yearly">{t('common:storefront.noYearly')}</p>
      )}
      {/* How it is billed, so a subscription never reads like a one-off purchase. */}
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500" data-testid="storefront-plan-renewal">
        <RefreshCw className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        {commitment > 0 ? t('checkout:contractTerm', { count: commitment }) : t('common:storefront.autoRenews')}
      </p>
    </>
  )
  /* The grid card lays each line on its own row so neighbours line up (below). */
  const yearlyParts = useYearlyPriceParts({
    option: option?.billingInterval === 'YEARLY' ? option : null,
    amount: Number(option?.price ?? 0),
    currency: plan.currency,
    saving: yearlySaving
  })
  const button = current ? (
    <ActionNote tone="success" testId="storefront-plan-current">{currentLabel}</ActionNote>
  ) : (
    <ActionButton label={actionLabel ?? t('common:storefront.subscribe')} onClick={subscribe} disabled={!plan.slug} testId="storefront-plan-subscribe" wide />
  )
  const action = warning && !current ? (
    <div>
      <p className="mb-2 flex items-start gap-1.5 text-xs text-red-700" data-testid="storefront-plan-warning">
        <AlertTriangle className="mt-px h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        <span>{warning}</span>
      </p>
      {button}
    </div>
  ) : button

  if (wide) {
    return (
      <StoreCardShell
        kind="plan"
        layout="wide"
        title={plan.name}
        cover={cover}
        description={list ? null : plan.description}
        highlighted={recommended}
        badge={recommended ? { label: t('common:storefront.recommended'), tone: 'primary' } : null}
        onOpen={subscribe}
        testId="storefront-plan-card"
        body={benefits}
        price={price}
        action={action}
      />
    )
  }

  return (
    <div
      role={current ? undefined : 'button'}
      tabIndex={current ? undefined : 0}
      aria-label={plan.name}
      onClick={subscribe}
      onKeyDown={current ? undefined : (e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          subscribe()
        }
      }}
      data-testid="storefront-plan-card"
      data-kind="plan"
      data-recommended={recommended || undefined}
      data-current={current || undefined}
      className={cn(
        /* Eight rows, one per line of the card (PLAN_CARD_ROWS). Inside a grid the card spans them as a
           subgrid, so the price, its second line, the tag, the renewal, the benefits and the button sit on
           the same line in every card of the row (David, 2026-10-30: "precisa alinhar"); alone, they stack. */
        'group relative row-span-8 grid h-full min-w-0 grid-rows-subgrid gap-y-0 rounded-2xl border bg-white p-6 shadow-md sm:snap-start transition-shadow hover:shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        !current && 'cursor-pointer',
        recommended ? 'border-primary shadow-lg ring-1 ring-primary' : current ? 'border-green-500 ring-1 ring-green-500' : 'border-gray-200'
      )}
    >
      {current && (
        <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-green-600 px-3 py-0.5 text-xs font-semibold text-white" data-testid="storefront-plan-current-badge">
          {currentLabel}
        </span>
      )}
      {recommended && (
        <span className="absolute -top-3 left-1/2 z-10 -translate-x-1/2 whitespace-nowrap rounded-full bg-primary px-3 py-0.5 text-xs font-semibold text-white">
          {t('common:storefront.recommended')}
        </span>
      )}
      {/* 1: cover, kind, name. Bottom-aligned: next to a card with a cover, the name still sits on its description. */}
      <div className="min-w-0 self-end">
        {cover && (
          <div className="relative -mx-6 -mt-6 mb-5 aspect-video overflow-hidden rounded-t-2xl bg-gray-100" data-testid="storefront-plan-cover">
            <CoverImage src={cover} alt="" className="absolute inset-0 h-full w-full object-cover" cdn={CARD_COVER_CDN} />
          </div>
        )}
        {!hideKind && <KindTag kind="plan" />}
        <h3 className={cn('truncate text-xl font-semibold text-gray-900 group-hover:text-primary', !hideKind && 'mt-2')} title={plan.name} data-plan-transition="name">{plan.name}</h3>
      </div>
      {/* 2: what it opens and one line of description, the line kept even when empty. */}
      <div className="mt-1 min-w-0">
        {!alone && (plan.unlocks?.length ?? 0) > 0 && (
          <p className="mb-0.5 flex items-center gap-1.5 text-xs text-gray-500" data-testid="storefront-plan-unlocks-count">
            <Layers className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
            {t('common:storefront.unlocksCount', { count: plan.unlocks?.length ?? 0 })}
          </p>
        )}
        <p className="min-h-5 truncate text-sm leading-5 text-gray-500" title={!list && plan.description ? plan.description : undefined}>
          {!list && plan.description ? plan.description : ''}
        </p>
      </div>
      {/* 3: the divider and the big price. */}
      <div className="mt-5 border-t border-gray-100 pt-5" data-plan-transition="price">
        {intervalSwitch && <div className="mb-3" onClick={stop} onKeyDown={stop}>{intervalSwitch}</div>}
        {trialDays > 0 && (
          <p className="mb-2 inline-flex rounded-full bg-green-50 px-2 py-0.5 text-xs font-medium text-green-700">
            {t('common:storefront.trialDays', { count: trialDays })}
          </p>
        )}
        {from && <p className="text-xs text-gray-500">{t('common:storefront.fromPrice')}</p>}
        {option?.billingInterval === 'YEARLY' ? yearlyParts.main : (
          <PriceLine
            amount={option ? formatMoney(Number(option.price), plan.currency) : '—'}
            suffix={periodKey ? t(periodKey) : option?.title}
            wide
          />
        )}
      </div>
      {/* 4: the price's second line ("no cartão (total …)", "equivale a …", or no yearly option). */}
      <div>
        {option?.billingInterval === 'YEARLY' ? yearlyParts.secondary : interval === 'YEARLY' && option && (
          <p className="mt-1 text-xs text-gray-500" data-testid="storefront-plan-no-yearly">{t('common:storefront.noYearly')}</p>
        )}
      </div>
      {/* 5: the saving tag (or what PIX and boleto pay up front). */}
      <div>{option?.billingInterval === 'YEARLY' ? yearlyParts.tag : null}</div>
      {/* 6: how it is billed, so a subscription never reads like a one-off purchase. */}
      <p className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500" data-testid="storefront-plan-renewal">
        <RefreshCw className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
        {commitment > 0 ? t('checkout:contractTerm', { count: commitment }) : t('common:storefront.autoRenews')}
      </p>
      {/* 7: what it includes. */}
      <div>{benefits}</div>
      {/* 8: the one button, at the bottom, and when access starts. */}
      <div className="self-end pt-6">
        {action}
        <AccessNote className="mt-2" hidden={current} />
      </div>
    </div>
  )
}

interface AddOnActionProps {
  addOn: StorefrontAddOn
  onAdd: () => void
  onUpgrade: () => void
  onSubscribe: () => void
  wide?: boolean
  /** Off on the card, which shows the requirement under the description so prices line up across a row. */
  showRequirement?: boolean
  /** `quiet` on the add-on rows: a complement, never as loud as a plan's "Subscribe". */
  weight?: 'strong' | 'quiet'
}

/**
 * How an add-on relates to a plan, for this viewer, from the server's
 * `availability`: which plan it needs (`upgrade`, `subscribe`), or that the
 * viewer's plan takes it (`add`). Null once it is owned.
 */
function useAddOnRequirement(addOn: StorefrontAddOn): { text: string; ok: boolean } | null {
  const { t } = useTranslation('common')
  const planNames = addOn.plans.map((plan) => plan.name).join(', ')
  if (addOn.availability === 'add') return { text: t('storefront.worksWithYourPlan'), ok: true }
  /* No public plan takes it: the button is disabled and there is no plan to name. */
  if ((addOn.availability === 'upgrade' || addOn.availability === 'subscribe') && addOn.plans.length > 0) {
    return { text: t('storefront.requiresPlan', { plans: planNames, count: addOn.plans.length }), ok: false }
  }
  return null
}

/** The dependency on a plan, as a chip. */
const Requirement = ({ requirement, className }: { requirement: { text: string; ok: boolean }; className?: string }) => (
  <p
    className={cn(
      'inline-flex max-w-full items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium',
      requirement.ok ? 'bg-green-50 text-green-700' : 'bg-gray-100 text-gray-700',
      className
    )}
    data-testid="storefront-addon-requirement"
  >
    {requirement.ok ? <Check className="h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" /> : <Layers className="h-3.5 w-3.5 flex-shrink-0 text-gray-500" aria-hidden="true" />}
    <span>{requirement.text}</span>
  </p>
)

/**
 * What this viewer can do with an add-on, from the server's `availability`:
 * the same block on the card and in the details dialog. An add-on is never
 * bought alone, so when it needs a plan the card says which, above the button
 * that leads there.
 */
export function AddOnAction({ addOn, onAdd, onUpgrade, onSubscribe, wide, showRequirement = true, weight = 'strong' }: AddOnActionProps) {
  const { t } = useTranslation('common')
  const { suspended: salesClosed } = useMemberEntry()
  const requirementInfo = useAddOnRequirement(addOn)
  const requirement = showRequirement && requirementInfo && !requirementInfo.ok ? <Requirement requirement={requirementInfo} className="mb-3" /> : null

  if (addOn.availability === 'owned') {
    return <ActionNote tone="success">{t('storefront.addOnOwned')}</ActionNote>
  }
  /* F-86: the community is suspended; nothing new is sold. */
  if (salesClosed) {
    return <ActionNote testId="storefront-addon-unavailable">{t('memberEntry.unavailable')}</ActionNote>
  }
  if (addOn.availability === 'add') {
    return <ActionButton label={t('storefront.addToPlan')} onClick={onAdd} testId="storefront-addon-action" wide={wide} weight={weight} />
  }
  if (addOn.availability === 'upgrade') {
    return (
      <div>
        {requirement}
        <ActionButton label={t('storefront.addOnUpgrade')} onClick={onUpgrade} testId="storefront-addon-action" wide={wide} weight={weight} />
      </div>
    )
  }
  return (
    <div>
      {requirement}
      <ActionButton
        label={t('storefront.addOnSubscribe')}
        onClick={onSubscribe}
        disabled={!addOn.plans[0]?.slug}
        testId="storefront-addon-action"
        wide={wide}
        weight={weight}
      />
    </div>
  )
}

interface AddOnStoreCardProps {
  /** Some card in the same grid has a cover image. */
  coverSlot?: boolean
  addOn: StorefrontAddOn
  layout?: StoreCardLayout
  /** The store's billing toggle and the add-on's yearly amount on the plans' yearly option (undefined: unknown). */
  interval?: string | null
  yearlyAmount?: number | null
  onOpen: () => void
  onAdd: () => void
  onUpgrade: () => void
  onSubscribe: () => void
}

/**
 * An add-on's price on the store's billing toggle: with "Anual", the yearly
 * amount the plan's yearly option charges for it (the same `addOnAmounts`
 * checkout adds), with its monthly equivalent; without one, the monthly price
 * and a short note. `yearlyAmount` undefined: not known (yet), shown as is.
 */
function useAddOnPrice(addOn: StorefrontAddOn, interval: string | null | undefined, yearlyAmount: number | null | undefined) {
  const { t } = useTranslation(['common', 'checkout'])
  const formatMoney = useMoney()
  if (interval === 'YEARLY' && typeof yearlyAmount === 'number') {
    return {
      amount: `+ ${formatMoney(yearlyAmount, addOn.currency)}`,
      suffix: t('common:storefront.addOnPerYear'),
      note: t('common:storefront.monthlyEquivalent', { price: formatMoney(yearlyAmount / 12, addOn.currency) })
    }
  }
  return {
    amount: `+ ${formatMoney(Number(addOn.price), addOn.currency)}`,
    suffix: t(addOn.billingInterval === 'YEARLY' ? 'common:storefront.addOnPerYear' : 'common:storefront.addOnPerMonth'),
    note: interval === 'YEARLY' && yearlyAmount === null ? t('common:storefront.noYearly') : null
  }
}

/**
 * An add-on (F-29 phase 3, criteria 5-8): the state comes from the server
 * (`availability`), never computed here. `add` opens the add-to-subscription
 * flow, `upgrade` the plan change, `subscribe` the plan's page; `owned` has
 * no button. The whole card opens the add-on's details.
 */
export function AddOnStoreCard({ addOn, layout = 'grid', coverSlot, interval, yearlyAmount, onOpen, onAdd, onUpgrade, onSubscribe }: AddOnStoreCardProps) {
  const { t } = useTranslation(['common', 'checkout'])
  const shown = useAddOnPrice(addOn, interval, yearlyAmount)
  const requirement = useAddOnRequirement(addOn)
  return (
    <StoreCardShell
      kind="addOn"
      layout={layout}
      coverSlot={coverSlot}
      title={addOn.title}
      description={addOn.shortDescription}
      cover={productCover(addOn)}
      highlighted={addOn.isHighlighted}
      badge={addOn.isHighlighted ? { label: t('checkout:purchase.featured'), tone: 'primary' } : null}
      onOpen={onOpen}
      testId="storefront-addon-card"
      data={{ availability: addOn.availability }}
      body={requirement ? <div className="mt-3"><Requirement requirement={requirement} /></div> : undefined}
      price={
        <>
          <PriceLine amount={shown.amount} suffix={shown.suffix} wide={layout === 'wide'} />
          {shown.note && <p className="mt-1 text-xs text-gray-500">{shown.note}</p>}
        </>
      }
      action={<AddOnAction addOn={addOn} onAdd={onAdd} onUpgrade={onUpgrade} onSubscribe={onSubscribe} wide={layout === 'wide'} showRequirement={false} />}
    />
  )
}

interface AddOnRowProps {
  addOn: StorefrontAddOn
  /** The store's billing toggle and the add-on's yearly amount on the plans' yearly option (undefined: unknown). */
  interval?: string | null
  yearlyAmount?: number | null
  onOpen: () => void
  onAdd: () => void
  onUpgrade: () => void
  onSubscribe: () => void
}

/**
 * An add-on as a compact row (David, 2026-10-30): it extends a plan, so it
 * never looks like a peer of one. Its image or the add-on icon on the left,
 * the name and what it adds, a chip with the plan it depends on, the price as
 * an increment ("+ R$ 450,00 por mês, somado ao plano") and a quieter button.
 * The state comes from the server (`availability`), never computed here; the
 * whole row opens the details.
 */
export function AddOnRow({ addOn, interval, yearlyAmount, onOpen, onAdd, onUpgrade, onSubscribe }: AddOnRowProps) {
  const { t } = useTranslation(['common', 'checkout'])
  const shown = useAddOnPrice(addOn, interval, yearlyAmount)
  const requirement = useAddOnRequirement(addOn)
  const cover = productCover(addOn)
  const price = (
    <p data-testid="storefront-addon-price">
      <span className="whitespace-nowrap text-lg font-semibold text-gray-900">{shown.amount}</span>
      <span className="block text-xs text-gray-500">{shown.suffix}</span>
      {shown.note && <span className="block text-xs text-gray-500">{shown.note}</span>}
    </p>
  )
  return (
    <div
      role="button"
      tabIndex={0}
      aria-label={addOn.title}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.target !== e.currentTarget) return
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onOpen()
        }
      }}
      data-testid="storefront-addon-card"
      data-kind="addOn"
      data-availability={addOn.availability}
      className={cn(
        'group flex cursor-pointer flex-col rounded-xl border bg-white transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        addOn.isHighlighted ? 'border-primary ring-1 ring-primary-200' : 'border-gray-200'
      )}
    >
      {/* What it is on top; below, the plan it depends on, then its price beside the button. The row is at
          most half the content width (two per line on desktop), so this stacked footer fits at any size. */}
      <div className="flex gap-4 p-4">
        <div className="relative h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg bg-secondary-50 text-secondary-700">
          {cover
            ? <CoverImage src={cover} alt="" cdn={{ w: 96, h: 96, fit: 'crop' }} className="absolute inset-0 h-full w-full object-cover" />
            : <Puzzle className="absolute left-1/2 top-1/2 h-5 w-5 -translate-x-1/2 -translate-y-1/2" aria-hidden="true" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 className="font-semibold text-gray-900 group-hover:text-primary">{addOn.title}</h3>
            {addOn.isHighlighted && (
              <span className="rounded-md bg-primary px-2 py-0.5 text-xs font-medium text-white">{t('checkout:purchase.featured')}</span>
            )}
          </div>
          {addOn.shortDescription && <p className="mt-0.5 line-clamp-2 text-sm text-gray-500">{addOn.shortDescription}</p>}
        </div>
      </div>
      <div className="mt-auto flex flex-col gap-3 border-t border-gray-100 px-4 py-3">
        {requirement && <Requirement requirement={requirement} className="self-start" />}
        <div className="flex items-center gap-3">
          <div className="min-w-0 flex-1">{price}</div>
          <div className="w-36 flex-shrink-0">
            <AddOnAction addOn={addOn} onAdd={onAdd} onUpgrade={onUpgrade} onSubscribe={onSubscribe} showRequirement={false} weight="quiet" />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * The left half next to a single plan (David, 2026-10-30), never an empty
 * column: what the plan opens (the spaces from `unlocks` the viewer can see,
 * up to six, each a link, then "e mais N"), or, when that is not known, how
 * the subscription works in three true lines. The benefits live in the card.
 *
 * `layout="row"` (the plans page, under its cards): the three lines side by
 * side in a strip with a small title, stacked on a phone.
 */
export function SubscriptionValuePanel({ plan, className, layout = 'stack' }: { plan?: StorefrontPlan | null; className?: string; layout?: 'stack' | 'row' }) {
  const { t } = useTranslation('common')
  const { sections } = useMenu()
  const unlocks = plan?.unlocks ?? []
  if (plan && unlocks.length > 0) {
    const shown = unlocks.slice(0, 6)
    return (
      <div className={className} data-testid="storefront-plan-unlocks">
        <h3 className="text-lg font-semibold text-gray-900 sm:text-xl">{t('storefront.unlocksTitle', { plan: plan.name })}</h3>
        <ul className="mt-5 grid gap-3 sm:grid-cols-2">
          {shown.map((space) => {
            const Icon = (space.icon && iconComponent(space.icon)) || moduleIcon(space.module ?? '')
            const path = spacePathOf(sections, space.id)
            const body = (
              <>
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-primary-700 shadow-sm">
                  <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
                </span>
                <span className="min-w-0 truncate text-sm font-medium text-gray-900" title={space.title}>{space.title}</span>
              </>
            )
            return (
              <li key={space.id}>
                {path
                  ? <AppLink to={path} className="flex items-center gap-3 rounded-lg p-1 hover:bg-white/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary">{body}</AppLink>
                  : <span className="flex items-center gap-3 p-1">{body}</span>}
              </li>
            )
          })}
        </ul>
        {unlocks.length > shown.length && (
          <p className="mt-3 text-sm text-gray-600">{t('storefront.unlocksMore', { count: unlocks.length - shown.length })}</p>
        )}
      </div>
    )
  }
  const items = [
    { key: 'ongoing', Icon: Infinity },
    { key: 'renews', Icon: RefreshCw },
    { key: 'instant', Icon: Zap }
  ] as const
  const row = layout === 'row'
  return (
    <div className={cn(row && 'rounded-2xl border border-gray-200 bg-gray-50 px-5 py-5 sm:px-6', className)} data-testid="storefront-plan-value" data-layout={layout}>
      <h3 className={row ? 'text-sm font-semibold text-gray-900' : 'text-lg font-semibold text-gray-900 sm:text-xl'}>{t('storefront.valuePanel.title')}</h3>
      <ul className={row ? 'mt-4 grid gap-4 sm:grid-cols-3 sm:gap-6' : 'mt-5 space-y-4'}>
        {items.map(({ key, Icon }) => (
          <li key={key} className="flex gap-3">
            <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-white text-primary-700 shadow-sm">
              <Icon className="h-[18px] w-[18px]" aria-hidden="true" />
            </span>
            <span className="text-sm text-gray-600">
              <span className="block font-medium text-gray-900">{t(`storefront.valuePanel.${key}.title`)}</span>
              {t(`storefront.valuePanel.${key}.text`)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * How buying works in this store (David, 2026-10-30): a slim strip near the
 * top, one compact item per kind the store lists (icon, title, one line of
 * about the same length). It stays: it cannot be closed (David, 2026-10-30).
 */
export function StoreExplainer({ kinds }: { kinds: readonly StoreItemKind[] }) {
  const { t } = useTranslation('common')
  if (kinds.length === 0) return null
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-4" data-testid="storefront-explainer">
      <p className="mb-3 text-sm font-semibold text-gray-900">{t('storefront.explainTitle')}</p>
      <ul className={cn('grid gap-4', kinds.length === 2 && 'md:grid-cols-2', kinds.length >= 3 && 'md:grid-cols-3')}>
        {kinds.map((kind) => {
          const Icon = KIND_ICON[kind]
          return (
            <li key={kind} className="flex gap-3">
              <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-700">
                <Icon className="h-4 w-4" aria-hidden="true" />
              </span>
              <span className="text-xs leading-5 text-gray-600">
                <span className="block text-sm font-medium text-gray-900">{t(`storefront.explain.${kind}.title`)}</span>
                {t(`storefront.explain.${kind}.text`)}
              </span>
            </li>
          )
        })}
      </ul>
    </div>
  )
}

/**
 * Three or more plans in one row (David, 2026-10-30: never 2+1). The cards
 * share the row (each 17.5-24rem); when they do not fit, the row scrolls
 * sideways, snapping each card to the start, so it opens on the first whole
 * card. The arrows sit above the row, never over a card, and the one with
 * nothing more on its side is disabled. On a phone the cards stack instead.
 */
export function PlanScroller({ children, count }: { children: ReactNode; count: number }) {
  const { t } = useTranslation('common')
  const ref = useRef<HTMLDivElement>(null)
  const [edges, setEdges] = useState({ start: true, end: true })
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const update = () => setEdges({ start: el.scrollLeft <= 1, end: el.scrollLeft + el.clientWidth >= el.scrollWidth - 1 })
    update()
    el.addEventListener('scroll', update, { passive: true })
    const observer = new ResizeObserver(update)
    observer.observe(el)
    return () => {
      el.removeEventListener('scroll', update)
      observer.disconnect()
    }
  }, [count])
  const move = (direction: 1 | -1) => ref.current?.scrollBy({ left: direction * ref.current.clientWidth * 0.8, behavior: 'smooth' })
  const scrolls = !(edges.start && edges.end)
  const arrow = (direction: 1 | -1) => {
    const disabled = direction < 0 ? edges.start : edges.end
    return (
      <button
        type="button"
        onClick={() => move(direction)}
        disabled={disabled}
        className="flex h-9 w-9 items-center justify-center rounded-full border border-gray-200 bg-white text-gray-700 shadow-sm hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-default disabled:border-gray-100 disabled:text-gray-300 disabled:shadow-none disabled:hover:bg-white"
        aria-label={t(direction < 0 ? 'storefront.scrollPrevious' : 'storefront.scrollNext')}
        data-testid={direction < 0 ? 'storefront-plans-previous' : 'storefront-plans-next'}
      >
        {direction < 0 ? <ChevronLeft className="h-5 w-5" aria-hidden="true" /> : <ChevronRight className="h-5 w-5" aria-hidden="true" />}
      </button>
    )
  }
  return (
    <div>
      {scrolls && (
        <div className="mb-3 hidden justify-end gap-2 sm:flex" data-testid="storefront-plans-arrows">
          {arrow(-1)}
          {arrow(1)}
        </div>
      )}
      <div className="relative">
        {/* A fade on the side with more cards, so a card cut at the edge reads as "there is more". */}
        {!edges.start && <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 left-0 z-10 hidden w-12 bg-gradient-to-r from-white to-transparent sm:block" />}
        {!edges.end && <span aria-hidden="true" className="pointer-events-none absolute inset-y-0 right-0 z-10 hidden w-16 bg-gradient-to-l from-white to-transparent sm:block" />}
        <div
          ref={ref}
          className="grid grid-cols-1 gap-x-6 gap-y-0 pt-3 [&>*]:mb-6 sm:snap-x sm:snap-mandatory sm:scroll-px-1 sm:auto-cols-[minmax(17.5rem,24rem)] sm:grid-flow-col sm:grid-cols-none sm:overflow-x-auto sm:px-1 sm:pb-3 sm:[justify-content:safe_center] sm:[scrollbar-width:thin]"
          data-testid="storefront-plans-row"
        >
          {children}
        </div>
      </div>
    </div>
  )
}

/**
 * The gaps of a grid of plan cards. The row gap is 0 and each card keeps a
 * bottom margin instead: the cards are subgrids of the grid's rows, and a row
 * gap there would also open between the lines inside every card.
 */
export const CARD_GRID_GAPS = 'gap-x-6 gap-y-0 [&>*]:mb-6'

interface PlanCardsLayoutProps {
  plans: StorefrontPlan[]
  /** One plan's card; `alone` when it is the only one (no "Recommended", the value panel beside it). */
  renderCard: (plan: StorefrontPlan, options: { alone: boolean }) => ReactNode
  /**
   * `store` (the Store): two plans centered, three (the recommended one in the middle) and five or more
   * in one row that scrolls when it does not fit, four in a row or 2x2. `compare` (the plans page, which
   * never scrolls sideways and shows at most three cards): two or three fill the width, in the order given.
   */
  mode?: 'store' | 'compare'
}

/**
 * The plan cards by how many there are, one rule for the Store and the plans
 * page (David, 2026-10-30). One plan: how the subscription works (or what the
 * plan opens) on the left, its card on the right. More: side by side, as
 * `mode` says. The cards line up line by line (PlanStoreCard's subgrid).
 */
export function PlanCardsLayout({ plans, renderCard, mode = 'store' }: PlanCardsLayoutProps) {
  const badges = plans.some(planIsRecommended)
  if (plans.length === 1) {
    const plan = plans[0]
    return (
      <div className="grid items-center gap-8 xl:grid-cols-[minmax(0,1fr)_24rem] xl:gap-12" data-testid="storefront-plans-table" data-count={1}>
        <SubscriptionValuePanel plan={plan} />
        <div className="w-full sm:mx-auto sm:max-w-sm">{renderCard(plan, { alone: true })}</div>
      </div>
    )
  }
  const cards = (list: StorefrontPlan[]) => list.map((plan) => <Fragment key={plan.id}>{renderCard(plan, { alone: false })}</Fragment>)
  if (mode === 'compare') {
    /* The viewer's own plan carries a badge on top too: the row always keeps room for one. */
    return (
      <div
        className={cn(CARD_GRID_GAPS, 'grid grid-cols-1 pt-3 sm:grid-cols-2', plans.length >= 3 && 'lg:grid-cols-3')}
        data-testid="storefront-plans-table"
        data-count={plans.length}
      >
        {cards(plans)}
      </div>
    )
  }
  /* Two plans: side by side, centered. */
  if (plans.length === 2) {
    return (
      <div className={cn(CARD_GRID_GAPS, 'mx-auto grid max-w-[49.5rem] grid-cols-1 sm:grid-cols-2', badges && 'pt-3')} data-testid="storefront-plans-table" data-count={2}>
        {cards(plans)}
      </div>
    )
  }
  /* Four plans: four in a row on a wide screen, 2x2 below. */
  if (plans.length === 4) {
    return (
      <div className={cn(CARD_GRID_GAPS, 'grid grid-cols-1 sm:grid-cols-2 min-[1400px]:grid-cols-4', badges && 'pt-3')} data-testid="storefront-plans-table" data-count={4}>
        {cards(plans)}
      </div>
    )
  }
  /* Three plans (the recommended one in the middle), or five and more: one row, scrolling when it does not fit. */
  const ordered = plans.length === 3 && plans.filter(planIsRecommended).length === 1
    ? (() => {
        const recommended = plans.find(planIsRecommended) as StorefrontPlan
        const others = plans.filter((plan) => plan !== recommended)
        return [others[0], recommended, others[1]]
      })()
    : plans
  return (
    <div data-testid="storefront-plans-table" data-count={plans.length}>
      <PlanScroller count={ordered.length}>{cards(ordered)}</PlanScroller>
    </div>
  )
}
